import { useEffect, useRef } from 'react'
import { toast } from 'react-toastify'

import { parseCookies } from 'nookies'

import { getReportTypeLabel } from 'utils/reports/reportTypeLabels'
import { toastProps } from 'utils/types/toast-props'

export type ReportStatus = 'pending' | 'processing' | 'generated' | 'error'

export interface ReportNotification {
  uuid: string
  status: ReportStatus
  model: string
  merchant_uuid: string
}

interface UseReportChannelProps {
  merchantUuid?: string
  onReportUpdate?: (notification: ReportNotification) => void
  enabled?: boolean
}

const getWebSocketURL = (): string => {
  return 'wss://back.clientbase.com.br/cable'
}

export const REPORT_CHANNEL_NAME = 'ReportChannel'

export const useReportChannel = ({
  merchantUuid,
  onReportUpdate,
  enabled = true
}: UseReportChannelProps = {}) => {
  const channelRef = useRef<ActionCable.Channel | null>(null)
  const consumerRef = useRef<ActionCable.Cable | null>(null)
  const isConnectedRef = useRef(false)
  const isConnectingRef = useRef(false)
  const hasAttemptedConnectionRef = useRef(false)
  const onReportUpdateRef = useRef(onReportUpdate)
  const notifiedReportsRef = useRef<Set<string>>(new Set())

  // Keep callback ref updated without triggering reconnection
  useEffect(() => {
    onReportUpdateRef.current = onReportUpdate
  }, [onReportUpdate])

  // Single effect to manage connection lifecycle
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!enabled) return
    if (!merchantUuid) return

    // Prevent multiple connection attempts
    if (
      isConnectedRef.current ||
      isConnectingRef.current ||
      hasAttemptedConnectionRef.current
    ) {
      return
    }

    const { ['nextauth.token']: token } = parseCookies()
    if (!token) return

    const setupConnection = async () => {
      isConnectingRef.current = true
      hasAttemptedConnectionRef.current = true

      try {
        const actionCable = await import('actioncable')
        const wsURL = `${getWebSocketURL()}?token=${token}`

        consumerRef.current = actionCable.default.createConsumer(wsURL)

        channelRef.current = consumerRef.current.subscriptions.create(
          { channel: REPORT_CHANNEL_NAME, merchant_uuid: merchantUuid },
          {
            received: (notification: ReportNotification) => {
              const notificationKey = `${notification.uuid}-${notification.status}`

              if (notifiedReportsRef.current.has(notificationKey)) {
                return
              }

              if (notification.status === 'generated') {
                notifiedReportsRef.current.add(notificationKey)
                const reportType = getReportTypeLabel(notification.model)
                toast.success(
                  `Relatório "${reportType}" disponível para download`,
                  { ...toastProps }
                )
              }

              if (notification.status === 'error') {
                notifiedReportsRef.current.add(notificationKey)
                const reportType = getReportTypeLabel(notification.model)
                toast.error(`Erro ao gerar relatório "${reportType}"`, {
                  ...toastProps
                })
              }

              onReportUpdateRef.current?.(notification)
            },
            connected: () => {
              isConnectedRef.current = true
              isConnectingRef.current = false
            },
            disconnected: () => {
              isConnectedRef.current = false
              isConnectingRef.current = false
              // Don't reset hasAttemptedConnectionRef - prevents reconnection loop
            }
          }
        )
      } catch (error) {
        console.error('Error connecting to ReportChannel:', error)
        isConnectingRef.current = false
      }
    }

    setupConnection()

    // Cleanup on unmount
    return () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe()
        channelRef.current = null
      }
      if (consumerRef.current) {
        consumerRef.current.disconnect()
        consumerRef.current = null
      }
      isConnectedRef.current = false
      isConnectingRef.current = false
      hasAttemptedConnectionRef.current = false
    }
  }, [enabled, merchantUuid])

  // Clear notified reports periodically
  useEffect(() => {
    const interval = setInterval(() => {
      notifiedReportsRef.current.clear()
    }, 5 * 60 * 1000)

    return () => clearInterval(interval)
  }, [])

  return {
    isConnected: isConnectedRef.current
  }
}

export default useReportChannel
